import {GlobalEventsMapForBackwardCompatibility, WinMeterFinishedValue} from '@gongxigames/framework/slot/events';
import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {SoundPlayer} from '@gongxigames/framework/slot/services/SoundPlayer';
import {BigWinView} from '@gongxigames/framework/slot/modules/bigwin/BigWinViewParticlesV5';
import {BigWinEvents} from '@gongxigames/framework/slot/modules/bigwin/BigWinEvents';
import {WinMeterEvents} from '@gongxigames/framework/slot/modules/winmeter';
import {FlowModuleEvents} from '@gongxigames/framework/slot/modules/flow';
import {SpinButtonEvents} from '@gongxigames/framework/slot/modules/spinbutton';
import {BigWinBasicState} from '@gongxigames/framework/slot/modules/bigwin/states/BigWinBasicState';
import {BigWinPlayingState} from '@gongxigames/framework/slot/modules/bigwin/states/BigWinPlayingState';
import {BigWinConstants} from '@gongxigames/framework/slot/modules/bigwin/BigWinConstants';

export class BigWinModule extends Module<BigWinView, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol('BigWinModule');

    public static readonly BASIC_STATE: string = 'basic';
    public static readonly PLAYING_STATE: string = 'playing';

    protected _isSkippableImmediately = false;
    protected _isPlayWinMeter = false;

    protected _countFrom = 0;
    protected _countTo = 0;
    protected _bigWinType!: string;

    protected setupViews(): void {
        this.setView(BigWinView.NAME);
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.onViewCurrentStateHandle(BigWinView.VIEW_BIG_WIN_FINISHED);
        this.onView(BigWinView.METER_FINISHED, this.onMeterFinished);
        this.onView(BigWinView.VIEW_POST_FEATURE_BW_SKIPPED, this.onBigWinSkipped);
        this.onView(BigWinView.METER_STARTED, this.onMeterStarted);
        this.onView(BigWinView.METER_FINAL_VALUE, this.onMeterFinalValue);
        this.onView(BigWinView.METER_SET, this.onMeterSet);

        this.on(FlowModuleEvents.WON_BIG_WIN, this.onBigWin);
        this.onCurrentStateHandle(SpinButtonEvents.SPIN_BUTTON_PRESSED);
        this.onCurrentStateHandle('General.UI.SPACEBAR_UP');

        this.on(FlowModuleEvents.BIG_WIN_AFTER_FREE_SPINS_STARTED, this.onBigWinAfterFreeSpins);
        this.on(FlowModuleEvents.BIG_WIN_AFTER_BONUS_GAME_STARTED, this.onBigWinAfterBonusGame);
        this.on(FlowModuleEvents.BIG_WIN_AFTER_RETRIGGER_STARTED, this.onBigWinAfterRetrigger);
    }

    protected onResourcesLoaded(): void {
        this._view && this.view.onResourcesLoaded();
    }

    protected setupStates(): void {
        this.addState(new BigWinBasicState(this, BigWinModule.BASIC_STATE));
        this.addState(new BigWinPlayingState(this, BigWinModule.PLAYING_STATE));

        this.gotoState(BigWinModule.BASIC_STATE);
    }

    protected onMeterFinished(): void {
        this.eventManager.emit(SoundPlayer.UNDUCK_MUSIC);
        this.eventManager.emit(BigWinEvents.WIN_METER_FINISHED, this.getWinMeterFinishedValue());
    }

    protected onBigWinSkipped(): void {
        this.eventManager.emit(BigWinEvents.POST_FEATURE_BW_SKIPPED, this.getWinMeterFinishedValue());
    }

    protected onMeterStarted(eventName: string, from: number, to: number, duration: number): void {
        this.eventManager.emit(WinMeterEvents.COUNT_UP, from, to, duration);
    }

    protected onMeterFinalValue(): void {
        this.eventManager.emit(WinMeterEvents.SNAP_TO_FINAL_VALUE);
    }

    protected onMeterSet(eventName: string, value: number): void {
        this.eventManager.emit(WinMeterEvents.SET_VALUE, value);
    }

    protected onBigWin(): void {
        this._isSkippableImmediately = false;
        this._isPlayWinMeter = true;
        this._countFrom = this.previousWin();
        this._countTo = this.logic.getTotalWin();
        this._bigWinType = this.getBigWinType();
        this.currentState.handle(FlowModuleEvents.WON_BIG_WIN);
    }

    protected onBigWinAfterFreeSpins(): void {
        this._isSkippableImmediately = false;
        this._isPlayWinMeter = false;
        this._countFrom = 0;
        this._countTo = this.logic.getTotalWin();
        this._bigWinType = this.getBigWinType();
        this.currentState.handle(FlowModuleEvents.WON_BIG_WIN);
    }

    protected onBigWinAfterBonusGame(): void {
        this._isSkippableImmediately = false;
        this._isPlayWinMeter = false;
        this._countFrom = 0;
        this._countTo = this.getCurrentRoundWin();
        this._bigWinType = this.getBigWinType();
        this.currentState.handle(FlowModuleEvents.WON_BIG_WIN);
    }

    protected onBigWinAfterRetrigger(): void {
        this._isSkippableImmediately = false;
        this._isPlayWinMeter = false;
        this._countFrom = this.previousWin();
        this._countTo = this.logic.getTotalWin();
        this._bigWinType = this.getBigWinType();
        this.currentState.handle(FlowModuleEvents.WON_BIG_WIN);
    }

    protected previousWin(): number {
        return this.logic.previousSpinRoundWin;
    }

    protected getCurrentRoundWin(): number {
        return this.getFeatureWin() +
            this.logic.getBaseWinFromTriggeringRound(this.convertPreviousActionToBonusID());
    }

    protected convertPreviousActionToBonusID(): number {
        return this.logic.previousAction - 1;
    }

    protected getFeatureWin(): number {
        return this.logic.getTotalWin() - this.previousWin();
    }

    protected getBigWinType(): string {
        const win = this.countTo - this.countFrom;
        const bet = this.logic.spinData.totalBet.value();
        const ratio = win / bet;

        if (ratio >= BigWinConstants.BIG_WIN[2]) {
            return BigWinConstants.BIG_WIN_TYPES[2];
        } else if (ratio >= BigWinConstants.BIG_WIN[1]) {
            return BigWinConstants.BIG_WIN_TYPES[1];
        }

        return BigWinConstants.BIG_WIN_TYPES[0];
    }

    public get isSkippableImmediately(): boolean {
        return this._isSkippableImmediately;
    }

    public get countFrom(): number {
        return this._countFrom;
    }

    public get countTo(): number {
        return this._countTo;
    }

    public get isPlayWinMeter(): boolean {
        return this._isPlayWinMeter;
    }

    public get bigWinType(): string {
        return this._bigWinType;
    }

    // TODO: Violated the DRY principle, this code is in module WinPresentationModule
    private getWinMeterFinishedValue(): WinMeterFinishedValue {
        const {previousSpinRoundWin} = this.logic;
        const totalWin = this.logic.getTotalWin();
        const win = totalWin - previousSpinRoundWin;

        return {win, totalWin, isBigWin: this.logic.isBigWin()};
    }
}
